// src/lib/intentionEditAccess.ts
//
// Central rules for who may edit a teaching intention, and which identity is the
// "real" actor when the current session is impersonating another staff member.
//
// Impersonation is view-as: the Supabase JWT stays the admin's, but the `user`
// object's role/email/staffCode become the TARGET's. So both the permission
// check and any audit write must resolve back to `user.originalUser` when
// impersonating — otherwise we'd gate on the target's role and the audit insert
// would violate the audit_logs RLS check (user_email must equal the JWT email).

import type { CustomUser } from '../contexts/AuthContext';

// Roles permitted to edit a submission from /intentions or the HR review screen.
// Matches AdminFillIntention (admin + assistant_principal). If HR reviewers use a
// different role, add it here.
export const INTENTION_EDIT_ROLES = new Set(['admin', 'assistant_principal']);

/** The real authenticated identity, unwrapping impersonation. */
export function realActor(user: CustomUser | null | undefined): {
  role?: string;
  email?: string;
  staffCode?: string;
} {
  if (!user) return {};
  const source = user.isImpersonating && user.originalUser ? user.originalUser : user;
  return { role: source.role, email: source.email, staffCode: source.staffCode };
}

/** Whether the current (real) user may edit intentions at all. */
export function canEditIntentions(user: CustomUser | null | undefined): boolean {
  const { role } = realActor(user);
  return !!role && INTENTION_EDIT_ROLES.has(role);
}
